import { LicenseType, SubscriptionStatus, UseSubscriptionCostType } from '@entities/billing';

import { LicensesModalAction } from './licensesModalReducer';

interface BillingData {
  requestCost: UseSubscriptionCostType[0];
  status: SubscriptionStatus;
  hasInactiveLicenses: boolean;
  unpaidLicenses: LicenseType[];
  renewableLicenses: LicenseType[];
  fullPeriodAmount?: number | null;
}

interface Props {
  companyId: string;
  billingData: BillingData | null;
  dispatch: (value: LicensesModalAction) => void;
}

const createLicensesModalSubscriptionCost = ({ companyId, billingData, dispatch }: Props) => {
  const resumeSubscriptionCost = async (count: number) => {
    if (!billingData) return { cost: 0, fullPeriodCost: 0 };

    const costResult = await billingData.requestCost.resumeCost(companyId, count);

    const resumeCost = costResult?.costWithDiscount || costResult?.cost || 0;
    const fullPeriodCost = costResult?.fullPeriodCost || 0;

    return { cost: billingData.status !== SubscriptionStatus.TrialSubscription ? resumeCost : 0, fullPeriodCost };
  };

  const extendSubscriptionCost = async (count: number) => {
    if (!billingData) return undefined;

    const costResult = await billingData.requestCost.extendCost(companyId, count);

    return costResult;
  };

  const getSubscriptionCost = async (licensesIds: string[]) => {
    if (!billingData) return { cost: 0, fullPeriodCost: 0 };

    const ids = licensesIds.filter((id) => billingData.unpaidLicenses.find((license) => license.id === id));

    if (billingData.hasInactiveLicenses) {
      const costResult = await resumeSubscriptionCost(ids.length);

      return {
        cost: costResult?.cost || 0,
        fullPeriodCost: (costResult?.fullPeriodCost || 0) * ids.length,
      };
    }

    const costResult = await extendSubscriptionCost(ids.length);

    return {
      cost: costResult?.cost || 0,
      fullPeriodCost: (costResult?.fullPeriodCost || 0) * (ids.length + billingData.renewableLicenses.length),
    };
  };

  const setSubscriptionCost = async (licensesIds: string[]) => {
    if (licensesIds.length > 0) {
      const costResult = await getSubscriptionCost(licensesIds);

      dispatch({
        type: 'setLicensesSubscriptionInfo',
        licensesSubscriptionCost: costResult?.cost || 0,
        licensesSubscriptionFullPeriodCost: costResult?.fullPeriodCost || 0,
      });
    } else {
      dispatch({
        type: 'setLicensesSubscriptionInfo',
        licensesSubscriptionCost: 0,
        licensesSubscriptionFullPeriodCost: billingData?.fullPeriodAmount || 0,
      });
    }
  };

  const setExtendSubscriptionCost = async (licensesIds: string[]) => {
    if (!billingData) return;

    const costResult = await billingData.requestCost.extendCost(companyId, licensesIds.length);

    dispatch({
      type: 'setLicensesSubscriptionInfo',
      licensesSubscriptionCost: costResult?.cost || 0,
      licensesSubscriptionFullPeriodCost: costResult?.fullPeriodCost || billingData.fullPeriodAmount || 0,
    });
  };

  return {
    resumeSubscriptionCost,
    setSubscriptionCost,
    setExtendSubscriptionCost,
  };
};

export default createLicensesModalSubscriptionCost;
